/**
 * MeasurementBrowser Component
 *
 * A tree-based browser for InfluxDB measurements and fields.
 * Allows selecting measurements and their fields for dataset creation.
 */

import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChevronRight, ChevronDown, Database, Loader2, Search, Check } from "lucide-react";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Badge } from "@/shared/ui/badge";
import { cn } from "@/shared/lib/utils";
import type { MeasurementFieldSelections } from "../../types/dataset";

interface FieldInfo {
  name: string;
  type: string;
}

interface MeasurementBrowserProps {
  dataSourceId: string;
  bucket: string;
  selections: MeasurementFieldSelections;
  onSelectionChange: (selections: MeasurementFieldSelections) => void;
  className?: string;
}

export function MeasurementBrowser({
  dataSourceId,
  bucket,
  selections,
  onSelectionChange,
  className,
}: MeasurementBrowserProps) {
  const [expandedMeasurements, setExpandedMeasurements] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [fieldCache, setFieldCache] = useState<Record<string, FieldInfo[]>>({});

  // Fetch measurements for the bucket
  const {
    data: measurements = [],
    isLoading: loadingMeasurements,
    error: measurementsError,
  } = useQuery({
    queryKey: ["influxdb", "measurements", dataSourceId, bucket],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("influxdb-browser", {
        body: { action: "measurements", dataSourceId, bucket },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return (data?.data || []) as string[];
    },
    enabled: !!dataSourceId && !!bucket,
  });

  // Fetch fields for a measurement
  const fetchFields = async (measurement: string): Promise<FieldInfo[]> => {
    if (fieldCache[measurement]) {
      return fieldCache[measurement];
    }

    const { data, error } = await supabase.functions.invoke("influxdb-browser", {
      body: { action: "fields", dataSourceId, bucket, measurement },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    const fields = (data?.data || []) as FieldInfo[];
    setFieldCache((prev) => ({ ...prev, [measurement]: fields }));
    return fields;
  };

  // Toggle measurement expansion
  const toggleMeasurement = async (measurement: string) => {
    const newExpanded = new Set(expandedMeasurements);
    if (newExpanded.has(measurement)) {
      newExpanded.delete(measurement);
    } else {
      newExpanded.add(measurement);
      // Fetch fields if not cached
      if (!fieldCache[measurement]) {
        try {
          await fetchFields(measurement);
        } catch (err) {
          console.error("Failed to fetch fields:", err);
        }
      }
    }
    setExpandedMeasurements(newExpanded);
  };

  // Toggle field selection
  const toggleField = (measurement: string, field: string) => {
    const currentFields = selections[measurement] || [];
    const isSelected = currentFields.includes(field);

    let newFields: string[];
    if (isSelected) {
      newFields = currentFields.filter((f) => f !== field);
    } else {
      newFields = [...currentFields, field];
    }

    const newSelections = { ...selections };
    if (newFields.length === 0) {
      delete newSelections[measurement];
    } else {
      newSelections[measurement] = newFields;
    }

    onSelectionChange(newSelections);
  };

  // Toggle all fields for a measurement
  const toggleAllFields = (measurement: string) => {
    const fields = fieldCache[measurement] || [];
    const currentFields = selections[measurement] || [];
    const allSelected = fields.length > 0 && currentFields.length === fields.length;

    const newSelections = { ...selections };
    if (allSelected) {
      delete newSelections[measurement];
    } else {
      newSelections[measurement] = fields.map((f) => f.name);
    }

    onSelectionChange(newSelections);
  };

  // Check if measurement matches search
  const matchesSearch = (measurement: string) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (measurement.toLowerCase().includes(query)) return true;
    // Also check cached fields
    const fields = fieldCache[measurement] || [];
    return fields.some((f) => f.name.toLowerCase().includes(query));
  };

  // Check if a specific field matches search
  const fieldMatchesSearch = (fieldName: string) => {
    if (!searchQuery) return true;
    return fieldName.toLowerCase().includes(searchQuery.toLowerCase());
  };

  // Filter measurements by search
  const filteredMeasurements = useMemo(() => {
    return measurements.filter(matchesSearch);
  }, [measurements, searchQuery, fieldCache]);

  // Filter fields within a measurement by search
  const getFilteredFields = (measurement: string) => {
    const fields = fieldCache[measurement] || [];
    if (!searchQuery) return fields;
    // If measurement name matches, show all fields
    if (measurement.toLowerCase().includes(searchQuery.toLowerCase())) {
      return fields;
    }
    // Otherwise filter fields
    return fields.filter((f) => fieldMatchesSearch(f.name));
  };

  // Count selected fields
  const selectedCount = Object.values(selections).reduce((acc, fields) => acc + fields.length, 0);

  // Reset selections when bucket changes
  useEffect(() => {
    onSelectionChange({});
    setExpandedMeasurements(new Set());
    setFieldCache({});
  }, [bucket, dataSourceId]);

  if (!dataSourceId || !bucket) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-muted-foreground p-4", className)}>
        <Database className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-sm">Select a data source to browse measurements</p>
      </div>
    );
  }

  if (loadingMeasurements) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full", className)}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-2">Loading measurements...</p>
      </div>
    );
  }

  if (measurementsError) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-destructive p-4", className)}>
        <Database className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-sm">Failed to load measurements</p>
        <p className="text-xs mt-1">{(measurementsError as Error).message}</p>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Header */}
      <div className="p-3 border-b space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">Measurements</h3>
          {selectedCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {selectedCount} field{selectedCount !== 1 ? "s" : ""} selected
            </Badge>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search measurements & fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        {searchQuery && (
          <p className="text-xs text-muted-foreground">
            Expand measurements to search their fields
          </p>
        )}
      </div>

      {/* Measurement List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {filteredMeasurements.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <p className="text-sm">No measurements found</p>
            </div>
          ) : (
            filteredMeasurements.map((measurement) => {
              const isExpanded = expandedMeasurements.has(measurement);
              const allFields = fieldCache[measurement] || [];
              const filteredFields = getFilteredFields(measurement);
              const selectedFields = selections[measurement] || [];
              const hasSelections = selectedFields.length > 0;
              const allSelected = allFields.length > 0 && selectedFields.length === allFields.length;

              return (
                <div key={measurement} className="rounded-md border bg-card">
                  {/* Measurement Header */}
                  <div
                    className={cn(
                      "flex items-center gap-2 p-2 cursor-pointer hover:bg-muted/50 rounded-t-md",
                      hasSelections && "bg-primary/5"
                    )}
                    onClick={() => toggleMeasurement(measurement)}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 p-0"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                    <Database className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium flex-1 truncate">
                      {measurement}
                    </span>
                    {hasSelections && (
                      <Badge variant="default" className="text-xs">
                        {selectedFields.length}
                      </Badge>
                    )}
                  </div>

                  {/* Fields */}
                  {isExpanded && (
                    <div className="border-t bg-muted/20 p-2 space-y-1">
                      {allFields.length === 0 ? (
                        <div className="flex items-center justify-center py-2">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          <span className="text-xs text-muted-foreground ml-2">
                            Loading fields...
                          </span>
                        </div>
                      ) : filteredFields.length === 0 ? (
                        <div className="text-center py-2">
                          <span className="text-xs text-muted-foreground">
                            No matching fields
                          </span>
                        </div>
                      ) : (
                        <>
                          {/* Select All */}
                          {!searchQuery && (
                            <div
                              className="flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleAllFields(measurement);
                              }}
                            >
                              <Checkbox
                                checked={allSelected}
                                className="h-4 w-4"
                              />
                              <span className="text-xs font-medium text-muted-foreground">
                                Select all ({allFields.length})
                              </span>
                            </div>
                          )}

                          {/* Field List */}
                          {filteredFields.map((field) => {
                            const isSelected = selectedFields.includes(field.name);
                            return (
                              <div
                                key={field.name}
                                className={cn(
                                  "flex items-center gap-2 p-1.5 rounded hover:bg-muted cursor-pointer ml-4",
                                  isSelected && "bg-primary/10"
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleField(measurement, field.name);
                                }}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  className="h-4 w-4"
                                />
                                <span className="text-xs flex-1 truncate">
                                  {field.name}
                                </span>
                                {isSelected && (
                                  <Check className="h-3 w-3 text-primary" />
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default MeasurementBrowser;
